import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import cn from 'classnames';
import { useResize } from '@nw/shared/ui';

import {
  DEFAULT_STYLES,
  MOBILE_BOTTOM_SHEET_BREAKPOINT,
  SEARCH_DEBOUNCE_MS
} from '../../constants';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';
import {
  readBodyInlineOverflow,
  writeBodyInlineOverflow
} from '../../utils/bodyInlineOverflow';
import {
  isMobileViewport,
  isNodeInsideBottomSheet
} from '../../utils/viewportHeightUtils';
import { ComboBoxProps } from '../../types';

import { ComboBoxAddItemButton } from '../ComboBoxAddItemButton';
import { ComboBoxDesktopDropdown } from '../ComboBoxDesktopDropdown';
import { ComboBoxEmptyState } from '../ComboBoxEmptyState';
import { ComboBoxLoadingState } from '../ComboBoxLoadingState';
import { ComboBoxMobileSheet } from '../ComboBoxMobileSheet';
import { ComboBoxOptionsList } from '../ComboBoxOptionsList';
import { ComboBoxSearchInput } from '../ComboBoxSearchInput';
import { ComboBoxTriggerField } from '../ComboBoxTriggerField';
import { FieldErrorMessage } from '../FieldErrorMessage';

export const ComboBox: React.FC<ComboBoxProps> = (props) => {
  const {
    name,
    label,
    value,
    placeholder = 'Search...',
    options,
    onSearch,
    isLoading = false,
    onChange,
    onBlur,
    onInputChange,
    error,
    helperText,
    isDisabled = false,
    isRequired = false,
    renderOption,
    getOptionLabel = (opt) => opt.label,
    getOptionValue = (opt) => opt.value,
    loadingText = 'Loading...',
    emptyStateText = "We couldn't find any matches.",
    customLoader,
    loadingMessage,
    className,
    inputClassName,
    labelClassName,
    optionClassName,
    showButton = true,
    buttonSize = 'md',
    onButtonClick,
    buttonClassName,
    placement = 'bottom',
    offset = 4,
    maxHeight = 344,
    closeOnScroll = true,
    shouldCloseOnBlur = true,
    defaultOpen = false,
    onOpenChange,
    enableAnimation = true,
    mobileBreakpoint = MOBILE_BOTTOM_SHEET_BREAKPOINT,
    loadingState,
    filterFn,
    caseSensitive = false,
    enableClientFilter = false
  } = props;

  const inputId = useId();
  const deviceWidth = useResize();
  const isMobile = isMobileViewport(deviceWidth, mobileBreakpoint);

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const listBoxRef = useRef<HTMLUListElement>(null);
  const mobileListRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  const isClosingRef = useRef(false);
  const isOpeningRef = useRef(false);
  const scrollGracePeriodRef = useRef<number | null>(null);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const justSelectedRef = useRef<string | null>(null);
  const enableClientFilterRef = useRef(enableClientFilter);
  const isTypingRef = useRef(false);
  const prevIsMobileRef = useRef(isMobile);

  useEffect(() => {
    enableClientFilterRef.current = enableClientFilter;
  }, [enableClientFilter]);

  const selectedOption = useMemo(
    () => options.find((opt) => getOptionValue(opt) === value),
    [options, value, getOptionValue]
  );

  useEffect(() => {
    if (isClosingRef.current) return;

    if (selectedOption && !isOpen) {
      setInputValue(getOptionLabel(selectedOption));
    } else if (!value && !isOpen) {
      setInputValue('');
    }
  }, [value, selectedOption, getOptionLabel, isOpen]);

  const filteredOptions = useMemo(() => {
    if (!enableClientFilter || !inputValue.trim()) {
      return options;
    }

    if (selectedOption) {
      const selectedLabel = getOptionLabel(selectedOption).trim();
      const trimmedInput = inputValue.trim();
      const inputCmp = caseSensitive ? trimmedInput : trimmedInput.toLowerCase();
      const labelCmp = caseSensitive ? selectedLabel : selectedLabel.toLowerCase();
      if (inputCmp === labelCmp) {
        return options;
      }
    }

    return options.filter((option) => {
      if (filterFn) {
        return filterFn(option, inputValue);
      }

      const labelText = getOptionLabel(option);
      const query = caseSensitive ? inputValue : inputValue.toLowerCase();
      const text = caseSensitive ? labelText : labelText.toLowerCase();

      return text.includes(query);
    });
  }, [
    options,
    inputValue,
    enableClientFilter,
    filterFn,
    caseSensitive,
    getOptionLabel,
    selectedOption
  ]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (isDisabled) return;

      if (open && isClosingRef.current) {
        return;
      }

      if (!open && isOpeningRef.current) {
        return;
      }

      if (!open) {
        isClosingRef.current = true;

        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = undefined;
        }

        setHighlightedIndex(-1);
        setIsOpen(false);
        onOpenChange?.(false);
        scrollGracePeriodRef.current = null;

        if (justSelectedRef.current) {
          setInputValue(justSelectedRef.current);
          justSelectedRef.current = null;
        } else if (selectedOption) {
          setInputValue(getOptionLabel(selectedOption));
        } else if (!value) {
          setInputValue('');
        }

        if (shouldCloseOnBlur && !isOpeningRef.current) {
          onBlur?.();
        }

        setTimeout(() => {
          inputRef.current?.blur();
          mobileInputRef.current?.blur();
          setIsInputFocused(false);
        }, 0);

        requestAnimationFrame(() => {
          setTimeout(() => {
            isClosingRef.current = false;
          }, 100);
        });
      } else {
        isClosingRef.current = false;
        isOpeningRef.current = true;

        if (enableClientFilter) {
          isTypingRef.current = false;
          if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = undefined;
          }
          if (selectedOption) {
            setInputValue(getOptionLabel(selectedOption));
          } else {
            setInputValue('');
          }
        } else if (selectedOption) {
          setInputValue(getOptionLabel(selectedOption));
        } else if (!isTypingRef.current) {
          if (value !== undefined && value !== null && value !== '') {
            setInputValue(String(value));
          } else {
            setInputValue('');
          }
        }

        setIsOpen(true);
        onOpenChange?.(true);
        scrollGracePeriodRef.current = Date.now();

        requestAnimationFrame(() => {
          setTimeout(() => {
            if (
              !isMobile &&
              inputRef.current &&
              document.activeElement !== inputRef.current
            ) {
              inputRef.current.focus();
            }
            setTimeout(() => {
              isOpeningRef.current = false;
            }, 350);
          }, 150);
        });
      }
    },
    [
      isDisabled,
      isMobile,
      onOpenChange,
      shouldCloseOnBlur,
      onBlur,
      selectedOption,
      getOptionLabel,
      enableClientFilter,
      value
    ]
  );

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange?.(optionValue);
      const selectedOpt = options.find(
        (opt) => getOptionValue(opt) === optionValue
      );
      if (selectedOpt) {
        const selectedLabel = getOptionLabel(selectedOpt);
        setInputValue(selectedLabel);
        justSelectedRef.current = selectedLabel;
      }
      handleOpenChange(false);
    },
    [onChange, options, getOptionValue, getOptionLabel, handleOpenChange]
  );

  const handleInputValueChange = useCallback(
    (val: string) => {
      isTypingRef.current = true;
      setInputValue(val);
      onInputChange?.(val);

      if (!isOpen && val.trim() && !isMobile) {
        handleOpenChange(true);
      }

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        if (!isClosingRef.current) {
          onSearch?.(val);
        }
        isTypingRef.current = false;
      }, SEARCH_DEBOUNCE_MS);
    },
    [isOpen, isMobile, handleOpenChange, onInputChange, onSearch]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    if (!isOpen && debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = undefined;
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && isMobile && mobileInputRef.current) {
      isOpeningRef.current = true;

      const focusAttempts = [
        () => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (mobileInputRef.current && isOpen) {
                mobileInputRef.current.focus();
              }
            }, 200);
          });
        },
        () => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (
                mobileInputRef.current &&
                isOpen &&
                document.activeElement !== mobileInputRef.current
              ) {
                mobileInputRef.current.focus();
              }
            }, 350);
          });
        },
        () => {
          requestAnimationFrame(() => {
            setTimeout(() => {
              if (
                mobileInputRef.current &&
                isOpen &&
                document.activeElement !== mobileInputRef.current
              ) {
                mobileInputRef.current.focus();
              }
              isOpeningRef.current = false;
            }, 500);
          });
        }
      ];

      focusAttempts.forEach((attempt) => attempt());
    } else if (!isOpen && isMobile) {
      isOpeningRef.current = false;
    }
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (!isOpen || !value) return;

    requestAnimationFrame(() => {
      setTimeout(() => {
        if (!isMobile && listBoxRef.current) {
          const selectedItem = listBoxRef.current.querySelector(
            `[role="option"][aria-selected="true"]`
          ) as HTMLElement;
          if (selectedItem) {
            selectedItem.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth'
            });
          }
        }

        if (isMobile && mobileListRef.current) {
          const selectedItem = mobileListRef.current.querySelector(
            `[role="option"][aria-selected="true"]`
          ) as HTMLElement;
          if (selectedItem) {
            selectedItem.scrollIntoView({
              block: 'nearest',
              behavior: 'smooth'
            });
          }
        }
      }, 50);
    });
  }, [isOpen, value, isMobile, filteredOptions]);

  const positionStyles = useDropdownPosition(
    containerRef,
    popoverRef,
    isOpen && !isMobile,
    placement,
    offset
  );

  useEffect(() => {
    if (!(isOpen && !isMobile)) return;

    const previousInlineOverflow = readBodyInlineOverflow();
    document.body.style.setProperty('overflow', 'hidden');

    return () => {
      writeBodyInlineOverflow(previousInlineOverflow);
    };
  }, [isOpen, isMobile]);

  useEffect(() => {
    if (prevIsMobileRef.current !== isMobile && isOpen) {
      handleOpenChange(false);
    }
    prevIsMobileRef.current = isMobile;
  }, [isMobile, isOpen, handleOpenChange]);

  useEffect(() => {
    if (!isOpen || isMobile || !closeOnScroll) return;

    const handleScroll = (event: Event) => {
      if (isOpeningRef.current) {
        return;
      }

      if (scrollGracePeriodRef.current !== null) {
        const gracePeriodMs = 700;
        const timeSinceOpen = Date.now() - scrollGracePeriodRef.current;

        if (timeSinceOpen < gracePeriodMs) {
          if (timeSinceOpen < 300) {
            return;
          }
        } else {
          scrollGracePeriodRef.current = null;
        }
      }

      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (mobileListRef.current?.contains(target)) return;
      if (isNodeInsideBottomSheet(target)) return;

      handleOpenChange(false);
    };

    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen, isMobile, closeOnScroll, handleOpenChange]);

  useEffect(() => {
    if (!isOpen || isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (isOpeningRef.current) {
        return;
      }

      const target = event.target as Node;
      if (
        containerRef.current?.contains(target) ||
        popoverRef.current?.contains(target)
      ) {
        return;
      }
      handleOpenChange(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, isMobile, handleOpenChange]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isDisabled || isClosingRef.current) return;

      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          if (isOpen && highlightedIndex >= 0) {
            const option = filteredOptions[highlightedIndex];
            if (option && !option.disabled) {
              handleSelect(getOptionValue(option));
            }
          } else if (!isOpen && !isMobile) {
            handleOpenChange(true);
          }
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!isOpen && !isMobile) {
            handleOpenChange(true);
          } else if (isOpen) {
            setHighlightedIndex((prev) => {
              const next = prev + 1;
              return next >= filteredOptions.length ? 0 : next;
            });
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!isOpen && !isMobile) {
            handleOpenChange(true);
          } else if (isOpen) {
            setHighlightedIndex((prev) => {
              const next = prev - 1;
              return next < 0 ? filteredOptions.length - 1 : next;
            });
          }
          break;
        case 'Escape':
          event.preventDefault();
          if (isOpen) {
            handleOpenChange(false);
            if (!isMobile) {
              inputRef.current?.focus();
            }
          }
          break;
        case 'Tab':
          if (isOpen) {
            handleOpenChange(false);
          }
          break;
      }
    },
    [
      isDisabled,
      isOpen,
      isMobile,
      highlightedIndex,
      filteredOptions,
      handleSelect,
      handleOpenChange,
      getOptionValue
    ]
  );

  const handleClear = useCallback(() => {
    setInputValue('');
    onChange?.('');
    onInputChange?.('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    onSearch?.('');
  }, [onChange, onSearch, onInputChange]);

  const handleOnButtonClick = () => {
    handleOpenChange(false);
    setTimeout(() => {
      inputRef.current?.blur();
      mobileInputRef.current?.blur();
      setIsInputFocused(false);
    }, 0);
    onButtonClick?.();
  };

  const isCurrentlyLoading = useMemo(() => {
    if (loadingState) {
      return (
        loadingState === 'loading' ||
        loadingState === 'filtering' ||
        loadingState === 'loadingMore'
      );
    }
    return isLoading;
  }, [loadingState, isLoading]);

  const hasError = Boolean(error);
  const showError = hasError && !isInputFocused && !isOpen;
  const showHelperText = (!hasError || isInputFocused || isOpen) && helperText;
  const showClearButton = Boolean(inputValue && !isDisabled);
  const shouldShowStickyButton = showButton && inputValue.trim().length > 0;

  const animationClasses = enableAnimation
    ? cn(
      'transition-all duration-200 ease-out',
      isOpen
        ? 'opacity-100 transform scale-100'
        : 'opacity-0 transform scale-95'
    )
    : '';

  const emptyState = (
    <ComboBoxEmptyState
      emptyStateText={emptyStateText}
      loadingState={loadingState}
    />
  );

  const loadingContent = (
    <ComboBoxLoadingState
      customLoader={customLoader}
      loadingMessage={loadingMessage}
      loadingText={loadingText}
    />
  );

  const optionsListProps = {
    filteredOptions,
    value,
    highlightedIndex,
    renderOption,
    getOptionLabel,
    getOptionValue,
    onSelect: handleSelect,
    onHighlight: setHighlightedIndex,
    optionClassName,
    emptyState
  };

  const addItemButton = shouldShowStickyButton ? (
    <ComboBoxAddItemButton
      inputValue={inputValue}
      buttonSize={buttonSize}
      buttonClassName={buttonClassName}
      onButtonClick={handleOnButtonClick}
      variant={isMobile ? 'bottomSheet' : 'dropdown'}
    />
  ) : undefined;

  return (
    <div className={cn(DEFAULT_STYLES.field, className)}>
      {label && (
        <label
          id={`${inputId}-label`}
          htmlFor={inputId}
          className={cn(
            DEFAULT_STYLES.field__label,
            isDisabled && DEFAULT_STYLES.field__labelDisabled,
            labelClassName,
            'informedInputLabelText'
          )}
        >
          {label}
          {isRequired && (
            <span className={DEFAULT_STYLES.field__labelRequired}>*</span>
          )}
        </label>
      )}

      <input type="hidden" name={name} value={value || ''} />

      <div className="relative">
        <ComboBoxTriggerField
          containerRef={containerRef}
          inputRef={inputRef}
          inputId={inputId}
          label={label}
          inputValue={inputValue}
          placeholder={placeholder}
          isMobile={isMobile}
          isOpen={isOpen}
          isDisabled={isDisabled}
          isInputFocused={isInputFocused}
          hasError={hasError}
          showClearButton={showClearButton}
          inputClassName={inputClassName}
          isClosingRef={isClosingRef}
          isOpeningRef={isOpeningRef}
          onInputValueChange={handleInputValueChange}
          onKeyDown={handleKeyDown}
          onClear={handleClear}
          onOpenChange={handleOpenChange}
          setIsInputFocused={setIsInputFocused}
        />

        {isOpen && !isMobile && (
          <ComboBoxDesktopDropdown
            inputId={inputId}
            label={label}
            isOpen={isOpen}
            containerRef={containerRef}
            popoverRef={popoverRef}
            positionStyles={positionStyles}
            animationClasses={animationClasses}
            maxHeight={maxHeight}
            shouldShowStickyButton={shouldShowStickyButton}
            isCurrentlyLoading={isCurrentlyLoading}
            loadingState={loadingState}
            loadingContent={loadingContent}
            optionsList={
              <ComboBoxOptionsList
                variant="desktop"
                listBoxRef={listBoxRef}
                inputId={inputId}
                label={label}
                maxHeight={maxHeight}
                shouldShowStickyButton={shouldShowStickyButton}
                {...optionsListProps}
              />
            }
            addItemButton={
              addItemButton ? (
                <ComboBoxAddItemButton
                  inputValue={inputValue}
                  buttonSize={buttonSize}
                  buttonClassName={buttonClassName}
                  onButtonClick={handleOnButtonClick}
                  variant="dropdown"
                />
              ) : undefined
            }
            onClose={() => handleOpenChange(false)}
          />
        )}

        {isOpen && isMobile && (
          <ComboBoxMobileSheet
            isOpen={isOpen}
            label={label}
            isClosingRef={isClosingRef}
            onClose={() => handleOpenChange(false)}
            searchInput={
              <ComboBoxSearchInput
                mobileInputRef={mobileInputRef}
                inputValue={inputValue}
                placeholder={placeholder}
                isDisabled={isDisabled}
                isOpen={isOpen}
                isMobile={isMobile}
                hasError={hasError}
                isInputFocused={isInputFocused}
                showClearButton={showClearButton}
                isFiltering={loadingState === 'filtering'}
                isClosingRef={isClosingRef}
                isOpeningRef={isOpeningRef}
                debounceRef={debounceRef}
                isTypingRef={isTypingRef}
                onInputChange={onInputChange}
                onSearch={onSearch}
                onClear={handleClear}
                setInputValue={setInputValue}
                setIsInputFocused={setIsInputFocused}
              />
            }
            optionsList={
              <ComboBoxOptionsList
                variant="mobile"
                mobileListRef={mobileListRef}
                shouldShowStickyButton={shouldShowStickyButton}
                {...optionsListProps}
              />
            }
            loadingContent={loadingContent}
            isCurrentlyLoading={isCurrentlyLoading}
            loadingState={loadingState}
            addItemButton={
              addItemButton ? (
                <ComboBoxAddItemButton
                  inputValue={inputValue}
                  buttonSize={buttonSize}
                  buttonClassName={buttonClassName}
                  onButtonClick={handleOnButtonClick}
                  variant="bottomSheet"
                />
              ) : undefined
            }
          />
        )}
      </div>

      {(hasError || showHelperText) && (
        <p
          id={`${inputId}-error`}
          className={cn(
            DEFAULT_STYLES.field__message,
            showError
              ? 'text-errorTextColor'
              : showHelperText
                ? 'text-helperTextColor'
                : 'invisible h-4'
          )}
          role={showError ? 'alert' : undefined}
        >
          {showError && error ? (
            <FieldErrorMessage message={error} />
          ) : showHelperText ? (
            helperText
          ) : (
            ''
          )}
        </p>
      )}
    </div>
  );
};

ComboBox.displayName = 'ComboBox';
